import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';

import slugify from 'common/util/slugify';
import makeFullName from 'common/util/makeFullName';

import {
  isVerificationOfEmploymentCompleted,
  isVerificationOfRentalCompleted,
} from 'common/householdApplication/applicationHelpers';

import {
  INCOME_VERIFICATION_STATUS_COMPLETED,
} from 'common/incomeVerificationConstants';

import {
  SR_API_ACTION_ADMIN_UPDATE_ITEM,
} from 'api/actionTypes';

import useBranch from 'client/hooks/useBranch';
import postWithState from 'client/actions/postWithState';

import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrLink from 'client/components/SrLink/SrLink';
import SrButton, {
  SR_BUTTON_THEME_ADOBE_BLUE,
  SR_BUTTON_THEME_ADOBE_YELLOW,
} from 'client/components/SrButton/SrButton';
import SrFormikField from 'client/components/SrFormikField';
import SrDarkModal from 'client/components/SrDarkModal';
import SrModalContent from 'client/components/SrModalContent';

export const SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_VOE = ' SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_VOE';
export const SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_VOR = ' SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_VOR';

const SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_TO_LINK = {
  [SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_VOE]: 'VOE',
  [SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_VOR]: 'VOR',
};

const SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_TO_TITLE = {
  [SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_VOE]: 'verification of employment',
  [SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_VOR]: 'verification of rental',
};

const SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_TO_ENV_VAR_TYPEFORM_ID = {
  [SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_VOE]: 'TYPEFORM_ID_VERIFICATION_OF_EMPLOYMENT',
  [SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_VOR]: 'TYPEFORM_ID_VERIFICATION_OF_RENTAL',
};

const SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_TO_FORM_ITEMS = {
  [SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_VOE]: 'verificationOfEmploymentTypeformResponse',
  [SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_VOR]: 'verificationOfRentalTypeformResponse',
};

const SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_TO_ADDITIONAL_DOCS_FIELD_ID = {
  [SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_VOE]: 'verificationOfEmploymentAdditionalDocuments',
  [SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_VOR]: 'verificationOfRentalAdditionalDocuments',
};

const SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_TO_VALIDATOR = {
  [SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_VOE]: isVerificationOfEmploymentCompleted,
  [SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_VOR]: isVerificationOfRentalCompleted,
};

const SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_TO_NULLIFY_FIELDS = {
  [SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_VOE]: [
    'verificationOfEmploymentPdfUrl',
    'verificationOfEmploymentPdfCreatedDate',
    'verificationOfEmploymentTypeformResponse',
  ],
  [SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_VOR]: [
    'verificationOfRentalPdfUrl',
    'verificationOfRentalPdfCreatedDate',
    'verificationOfRentalTypeformResponse',
  ],
};

export const getFormField = ({ typeformId, renterProfile }) => {
  const formFieldId = SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_TO_ADDITIONAL_DOCS_FIELD_ID[typeformId];
  const formValue = renterProfile[formFieldId] || [];
  const userNameSlug = slugify(makeFullName(renterProfile.firstName, renterProfile.lastName));

  const formField = {
    id: formFieldId,
    label: '',
    type: 'file_multi',
    properties: {
      fileAccept: '*',
      filePath: `/renter-application-uploads/${userNameSlug}-${slugify(SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_TO_LINK[typeformId])}-${(formValue).length + 1}-{uuid}`, // eslint-disable-line max-len
      isPrivate: true,
      uploadAs: renterProfile.userId,
    },
  };

  return formField;
};

const SrHouseholdApplicationTypeform = ({
  typeformId,
  renterProfile,
  dispatch,
  onUpdateHousehold,
  incomeVerification,
  acceptBankDataAsVoe,
}) => {
  const [isModalOpen, setModalIsOpen] = useState(false);
  const { TYPEFORM_ID } = useBranch({
    TYPEFORM_ID: ['env', SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_TO_ENV_VAR_TYPEFORM_ID[typeformId]],
  });
  const typeformUrl = `https://sunroom.typeform.com/to/${TYPEFORM_ID}?user_id=${renterProfile.userId}`;
  const typeformAnswers = renterProfile[SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_TO_FORM_ITEMS[typeformId]] || [];
  const formField = getFormField({
    typeformId,
    renterProfile,
  });

  const onToggleModal = () => {
    setModalIsOpen(!isModalOpen);
  };

  const onSubmit = (formValues) => {
    const additionalDocuments = formValues[formField.id];

    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_UPDATE_ITEM,
      queryId: 'additionalDocuments',
      payload: {
        table: 'householdApplications',
        where: {
          userId: renterProfile.userId,
        },
        updates: {
          [formField.id]: additionalDocuments,
        },
      },
    }).then(() => {
      onUpdateHousehold();
      setModalIsOpen(false);
    });
  };

  const onRemoveTypeformAnswers = () => {
    if (!global.confirm('Are you sure you want to remove the answers?')) {
      return;
    }

    const fieldsToNullify = SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_TO_NULLIFY_FIELDS[typeformId]
      .reduce((memo, fieldName) => ({
        ...memo,
        [fieldName]: null,
      }), {});

    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_UPDATE_ITEM,
      queryId: 'removeTypeformAnswers',
      payload: {
        table: 'householdApplications',
        where: {
          userId: renterProfile.userId,
        },
        updates: fieldsToNullify,
      },
    }).then(() => {
      onUpdateHousehold();
      setModalIsOpen(false);
    });
  };

  const completed = SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_TO_VALIDATOR[typeformId]({
    householdApplication: renterProfile,
  });

  const shouldShowIncomeVerificationBody = (
    acceptBankDataAsVoe
    && incomeVerification
    && incomeVerification?.status === INCOME_VERIFICATION_STATUS_COMPLETED
    && incomeVerification.incomeMonthly > 0
  );

  return (
    <SrText padding='0 4px'>
      <SrText
        color='blue300'
        weight='500'
        cursor='pointer'
        onClick={renterProfile.userId ? onToggleModal : null}
        opacity={completed || shouldShowIncomeVerificationBody ? '1' : '0.25'}
      >
        {SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_TO_LINK[typeformId]}
      </SrText>
      {isModalOpen && (
        <SrDarkModal>
          <SrModalContent style={{ minWidth: 700, marginTop: 50 }} onCloseIconClick={onToggleModal}>
            <SrText font='whitney-sc' weight='800' size='23' align='center' display='block'>
              {SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_TO_TITLE[typeformId]}
            </SrText>
            <SrBox marginTop='20px'>
              {
                shouldShowIncomeVerificationBody
                  ? (
                    <SrText font='greycliff' weight='400' fontSize='17px' lineHeight='28px'>
                      No verification of employment needed. Income proven using Pinwheel.
                    </SrText>
                  )
                  : (
                    <TypeformBody
                      typeformUrl={typeformUrl}
                      typeformAnswers={typeformAnswers}
                      onRemoveTypeformAnswers={onRemoveTypeformAnswers}
                    />
                  )
              }
              <Formik
                enableReinitialize
                initialValues={{
                  [formField.id]: renterProfile[formField.id] || [],
                }}
                onSubmit={onSubmit}
                render={(formikBag) => {
                  return (
                    <Form>
                      <SrText font='whitney-sc' weight='500' color='blue400' display='block'>
                        additional documents
                      </SrText>
                      <SrFormikField
                        formikBag={formikBag}
                        formField={formField}
                      />
                      <SrButton
                        type='submit'
                        theme={SR_BUTTON_THEME_ADOBE_YELLOW}
                        disabled={!formikBag.isValid}
                      >
                        Upload
                      </SrButton>
                    </Form>
                  );
                }}
              />
            </SrBox>
          </SrModalContent>
        </SrDarkModal>
      )}
    </SrText>
  );
};

const TypeformBody = ({
  typeformUrl,
  typeformAnswers,
  onRemoveTypeformAnswers,
}) => {
  return (
    <>
      <SrText font='whitney-sc' weight='500' color='blue400' display='block'>typeform link</SrText>
      <SrLink to={typeformUrl} target='_blank'>
        <SrText color='blue300'>
          {typeformUrl}
        </SrText>
      </SrLink>
      <SrBox margin='20px 0'>
        <SrText font='whitney-sc' weight='500' color='blue400' display='block'>
          typeform answers
        </SrText>
        {typeformAnswers.map(({ id, title, value }, i) => {
          return (
            <SrBox key={id} marginTop='5px'>
              <SrText weight='500' display='block'>{i + 1 }. {title}</SrText>
              <SrText paddingLeft='10px'>{value}</SrText>
            </SrBox>
          );
        })}
        {typeformAnswers.length === 0 && (
          <SrBox marginTop='5px'>
            <SrText>--</SrText>
          </SrBox>
        )}
        {typeformAnswers.length > 0 && (
          <SrBox marginTop='5px'>
            <SrButton onClick={onRemoveTypeformAnswers} theme={SR_BUTTON_THEME_ADOBE_BLUE}>
              Remove answers
            </SrButton>
          </SrBox>
        )}
      </SrBox>
    </>
  );
};

SrHouseholdApplicationTypeform.propTypes = {
  typeformId: PropTypes.oneOf([
    SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_VOE,
    SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_VOR,
  ]).isRequired,
  renterProfile: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  onUpdateHousehold: PropTypes.func.isRequired,
  incomeVerification: PropTypes.object,
  acceptBankDataAsVoe: PropTypes.bool,
};

SrHouseholdApplicationTypeform.displayName = 'SrHouseholdApplicationTypeform';

export default SrHouseholdApplicationTypeform;
